/**
 * @typedef {Object} BundleVariant
 * @property {string|number} id
 * @property {boolean|string} [available]
 * @property {number|string} [inventory_quantity]
 * @property {string[]} [options]
 * @property {string} [title]
 * @property {string} [option1]
 * @property {string} [option2]
 * @property {string} [option3]
 * @property {number|string} [price]
 * @property {number|string|null} [compare_at_price]
 */

/**
 * VariantRadiosController
 */
class VariantRadiosController {
  /**
   * @param {HTMLElement} root - <bundle-variant-radios> element
   */
  constructor(root) {
    /** @type {HTMLElement} */
    this.root = root;

    /** @type {BundleVariant[]} */
    this.variants = this._parseVariantsJson();

    /** @type {BundleVariant|null} */
    this.selectedVariant = null;

    /** @type {HTMLButtonElement|null} */
    this.addToCartButton = this._findAddToCartButton();

    /** @type {HTMLSpanElement|null} */
    this.legendLabelEl = this._findLegendElement();

    /** @type {boolean} */
    this._isUnavailableSelection = false;

    this._onChange = this._onChange.bind(this);
  }

  /**
   * Initialize the controller.
   */
  init() {
    if (!this.variants.length) {
      console.warn(
        '[VariantRadiosController] No variants JSON found for',
        this.root
      );
      return;
    }

    this._updateOptionStates();

    this.root.addEventListener('change', this._onChange);

    // Ensure initial state is consistent with pre-checked radio
    const radios = this._getRadios();
    const initial = radios.find((r) => r.checked) || radios[0];
    if (initial) {
      const isAvailable = this._isSizeAvailable(initial.value);
      this._applySelection(initial, isAvailable, { silent: true });
    }
  }

  /**
   * Tear down listeners.
   */
  destroy() {
    this.root.removeEventListener('change', this._onChange);
  }

  /**
   * Handle change events on radio inputs.
   * @param {Event} event
   * @private
   */
  _onChange(event) {
    const target = /** @type {HTMLInputElement|null} */ (event.target);
    if (!target || target.type !== 'radio') return;

    const value = target.value;
    const isAvailable = this._isSizeAvailable(value);

    this._applySelection(target, isAvailable);
  }

  /**
   * Parse the <script type="application/json"> inside this root.
   * Expects an array of variants (bundle_product.variants | json).
   *
   * @returns {BundleVariant[]}
   * @private
   */
  _parseVariantsJson() {
    const script = this.root.querySelector('script[type="application/json"]');
    if (!script || !script.textContent) return [];

    try {
      const json = JSON.parse(script.textContent.trim());
      if (Array.isArray(json)) {
        return json;
      }
      console.warn(
        '[VariantRadiosController] Expected variants JSON array, got:',
        json
      );
      return [];
    } catch (error) {
      console.error(
        '[VariantRadiosController] Failed to parse variants JSON',
        error
      );
      return [];
    }
  }

  /**
   * Find the Add to Cart button
   * @returns {HTMLButtonElement|null}
   * @private
   */
  _findAddToCartButton() {
    const button = document.querySelector('.product-form__submit');
    if (button instanceof HTMLButtonElement) return button;

    return null;
  }

  /**
   * Get all radio inputs within this component.
   * @returns {HTMLInputElement[]}
   * @private
   */
  _getRadios() {
    return Array.from(this.root.querySelectorAll('input[type="radio"]')).filter(
      (el) => el instanceof HTMLInputElement
    );
  }

  /**
   * Find label associated with given radio.
   * @param {HTMLInputElement} radio
   * @returns {HTMLLabelElement|null}
   * @private
   */
  _getLabelFor(radio) {
    const id = radio.id;
    if (!id) return null;

    const label = this.root.querySelector(`label[for="${CSS.escape(id)}"]`);
    return label instanceof HTMLLabelElement ? label : null;
  }

  /**
   * Find the span that should display the selected size
   * inside legend.
   *
   * @returns {HTMLSpanElement|null}
   * @private
   */
  _findLegendElement() {
    const span = this.root.querySelector('legend .size__label');
    return span instanceof HTMLSpanElement ? span : null;
  }

  /**
   * Update legend label text when a size is chosen.
   *
   * @param {string} sizeValue
   * @private
   */
  _updateLegendLabel(sizeValue) {
    if (!this.legendLabelEl) return;

    const size = String(sizeValue || '').trim();

    if (!size) {
      this.legendLabelEl.textContent = '';
      return;
    }

    this.legendLabelEl.textContent = size;
  }

  /**
   * Determine whether a size value is available,
   * based on variant availability and inventory quantity.
   *
   * NOTE: There might be other options (color, etc.) but they are assumed
   * to be fixed / pre-selected, so we only check if *any* variant that
   * includes this size value is available.
   *
   * @param {string} sizeValue
   * @returns {boolean}
   * @private
   */
  _isSizeAvailable(sizeValue) {
    const normalizedValue = String(sizeValue).trim();

    return this.variants.some((variant) => {
      if (!variant) return false;

      const options = Array.isArray(variant.options)
        ? variant.options
        : [variant.option1, variant.option2, variant.option3].filter(Boolean);

      const hasSizeMatch = options.some(
        (opt) => String(opt).trim() === normalizedValue
      );
      if (!hasSizeMatch) return false;

      const available = variant.available === true;

      const qtyRaw = variant.inventory_quantity;
      let qty = Number.NaN;
      if (typeof qtyRaw === 'number') {
        qty = qtyRaw;
      } else if (typeof qtyRaw === 'string' && qtyRaw.trim() !== '') {
        qty = Number.parseInt(qtyRaw, 10);
      }

      const hasStock = Number.isNaN(qty) ? true : qty > 0;

      return available && hasStock;
    });
  }

  /**
   * Update all option labels/inputs with unavailable state.
   * @private
   */
  _updateOptionStates() {
    const radios = this._getRadios();

    radios.forEach((radio) => {
      const label = this._getLabelFor(radio);
      const isAvailable = this._isSizeAvailable(radio.value);

      radio.dataset.unavailable = (!isAvailable).toString();

      if (!label) return;

      if (!isAvailable) {
        label.classList.add('bundle-size--unavailable', 'strike-thru');
        label.setAttribute('aria-disabled', 'true');
      } else {
        label.classList.remove('bundle-size--unavailable', 'strike-thru');
        label.removeAttribute('aria-disabled');
      }
    });
  }

  /**
   * Apply selection rules when a size is chosen.
   * - Marks selected radio as checked
   * - Updates legend label
   * - Updates price data attributes
   * - Toggles Add to Cart availability
   * - Dispatches events for external listeners
   *
   * @param {HTMLInputElement} radio
   * @param {boolean} isAvailable
   * @param {{ silent?: boolean }} [opts]
   * @private
   */
  _applySelection(radio, isAvailable, { silent = false } = {}) {
    radio.checked = true;

    const variant = this._findVariantForSize(radio.value) || null;
    this.selectedVariant = variant;

    if (variant && variant.id != null) {
      this.root.dataset.selectedVariantId = String(variant.id);
    }

    this._updatePriceAttributes(variant);
    this._updateLegendLabel(radio.value);

    if (!isAvailable) {
      this._setUnavailableState(true);
      return;
    }

    this._setUnavailableState(false);

    if (!silent) {
      const variant = this._findVariantForSize(radio.value);
      this._dispatchEvent('variant:size-change', {
        value: radio.value,
        variantId: variant?.id ?? null,
        available: isAvailable,
        rootId: this.root.id || null,
      });
    }
  }

  /**
   * Update price data attributes when variant changes.
   * @param {BundleVariant|null} variant
   * @private
   */

  _updatePriceAttributes(variant) {
    if (!variant) return;

    const price = Number(variant.price || 0);
    const compareAtPrice = variant.compare_at_price
      ? Number(variant.compare_at_price)
      : 0;

    if (price > 0) {
      this.root.dataset.productPrice = String(price);
    }

    const msrp = compareAtPrice > 0 ? compareAtPrice : price;
    if (msrp > 0) {
      this.root.dataset.msrp = String(msrp);
    }
  }

  /**
   * Find the first variant matching the given size value.
   * @param {string} sizeValue
   * @returns {BundleVariant|null}
   * @private
   */
  _findVariantForSize(sizeValue) {
    const normalizedValue = String(sizeValue).trim();

    return (
      this.variants.find((variant) => {
        const options = Array.isArray(variant.options)
          ? variant.options
          : [variant.option1, variant.option2, variant.option3].filter(Boolean);

        return options.some((opt) => String(opt).trim() === normalizedValue);
      }) || null
    );
  }

  /**
   * Toggle Add to Cart disabled / text for unavailable selection.
   * @param {boolean} isUnavailable
   * @private
   */
  _setUnavailableState(isUnavailable) {
    this._isUnavailableSelection = isUnavailable;

    if (isUnavailable) {
      this.root.setAttribute('data-selection-unavailable', 'true');
    } else {
      this.root.removeAttribute('data-selection-unavailable');
    }

    const allGood = this._allSelectionsAvailable();

    // store-closed comes from team-store-bundle-cart.js _checkStoreClosedState() and shouldn't be overriden
    // TODO: move add to cart button management to a separate global class
    if (
      this.addToCartButton &&
      !this.addToCartButton.hasAttribute('store-closed') &&
      !this.addToCartButton.hasAttribute('data-team-store-context-locked') 
    ) {
      if (!allGood) {
        this.addToCartButton.disabled = true;
        this.addToCartButton.setAttribute('aria-disabled', 'true');
        this.addToCartButton.textContent = window.variantStrings.unavailable;
      } else {
        this.addToCartButton.disabled = false;
        this.addToCartButton.setAttribute('aria-disabled', 'false');
        this.addToCartButton.textContent = window.variantStrings.addToCart;
      }
    }
  }

  /**
   * Check if ALL bundle items have valid selection
   * @returns {boolean}
   * @private
   */
  _allSelectionsAvailable() {
    const roots = document.querySelectorAll('bundle-variant-radios');

    return Array.from(roots).every((root) => {
      const variantId = root.getAttribute('data-selected-variant-id');
      const isUnavailable =
        root.getAttribute('data-selection-unavailable') === 'true';

      return variantId && !isUnavailable;
    });
  }

  /**
   * Dispatch a custom event from the root element.
   * @param {string} type
   * @param {Record<string, any>} detail
   * @private
   */
  _dispatchEvent(type, detail) {
    const event = new CustomEvent(type, {
      bubbles: true,
      cancelable: false,
      detail,
    });

    this.root.dispatchEvent(event);
  }
}

/**
 * VariantRadiosElement
 */
class VariantRadiosElement extends HTMLElement {
  constructor() {
    super();
    /** @type {VariantRadiosController|null} */
    this._controller = null;
  }

  connectedCallback() {
    if (this._controller) return;
    this._controller = new VariantRadiosController(this);
    this._controller.init();
  }

  disconnectedCallback() {
    if (this._controller) {
      this._controller.destroy();
      this._controller = null;
    }
  }
} 

if (!customElements.get('bundle-variant-radios')) {
  customElements.define('bundle-variant-radios', VariantRadiosElement);
}
